import React, { createContext, useContext, useMemo, useState } from 'react';

export interface DateRange { from: Date; to: Date; }

interface GlobalDateRangeContextType {
  globalRange: DateRange;
  setGlobalRange: (range: DateRange) => void;
}

const GlobalDateRangeContext = createContext<GlobalDateRangeContextType | undefined>(undefined);

function defaultRange(): DateRange {
  const today = new Date();
  const monthStart = new Date();
  monthStart.setDate(1);
  return { from: monthStart, to: today };
}

/**
 * One shared date range, driven by the Overview page's picker. Every other module (except
 * Scorecard, which intentionally keeps its own independent Weekly/Monthly/Quarterly + calendar
 * state) reads this as its default range, and re-syncs to it whenever it changes on Overview —
 * while still letting the user manually pick a different range locally on any individual module.
 */
export const GlobalDateRangeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [globalRange, setGlobalRange] = useState<DateRange>(defaultRange);
  const value = useMemo(() => ({ globalRange, setGlobalRange }), [globalRange]);
  return <GlobalDateRangeContext.Provider value={value}>{children}</GlobalDateRangeContext.Provider>;
};

export function useGlobalDateRange(): GlobalDateRangeContextType {
  const ctx = useContext(GlobalDateRangeContext);
  if (!ctx) throw new Error('useGlobalDateRange must be used within a GlobalDateRangeProvider');
  return ctx;
}

/**
 * Clamps a range (typically the current global range) into a module's own pickable window —
 * some modules sync a shorter lookback than Overview's 90 days (e.g. HubSpot/DTC KPIs cap at 60).
 * Used to seed a module's local range from the global one without ever requesting dates outside
 * what that module actually has synced.
 */
export function clampRangeToBounds(range: DateRange, minDate: Date, maxDate: Date, maxSpanDays?: number): DateRange {
  let from = range.from < minDate ? minDate : range.from > maxDate ? maxDate : range.from;
  let to = range.to < minDate ? minDate : range.to > maxDate ? maxDate : range.to;
  if (to < from) to = from;
  if (maxSpanDays && to.getTime() - from.getTime() > maxSpanDays * 86400000) {
    from = new Date(to.getTime() - maxSpanDays * 86400000);
    if (from < minDate) from = minDate;
  }
  return { from, to };
}
